import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { RefreshCw, Save, Loader2, Info } from 'lucide-react';
import { companyApi, RepeatedServiceSettings as RepeatedServiceSettingsType } from '@/services/companyApi';

export default function RepeatedServiceSettings() {
  const queryClient = useQueryClient();
  const [daysWindow, setDaysWindow] = useState('30');
  const [requireFaultMatch, setRequireFaultMatch] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['repeatedServiceSettings'],
    queryFn: companyApi.getRepeatedServiceSettings,
  });

  const updateMutation = useMutation({
    mutationFn: (data: RepeatedServiceSettingsType) => companyApi.updateRepeatedServiceSettings(data),
    onSuccess: () => {
      toast.success('Repeated service settings updated successfully');
      queryClient.invalidateQueries({ queryKey: ['repeatedServiceSettings'] });
      setHasChanges(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update settings');
    },
  });

  useEffect(() => {
    if (settings) {
      setDaysWindow(String(settings.repeatedServiceDaysWindow));
      setRequireFaultMatch(settings.repeatedServiceRequireFaultMatch);
    }
  }, [settings]);

  const checkChanges = (newDays: string, newRequireFault: boolean) => {
    if (!settings) return;
    setHasChanges(
      newDays !== String(settings.repeatedServiceDaysWindow) ||
      newRequireFault !== settings.repeatedServiceRequireFaultMatch
    );
  };

  const handleDaysChange = (value: string) => {
    setDaysWindow(value);
    checkChanges(value, requireFaultMatch);
  };

  const handleFaultMatchToggle = () => {
    const newValue = !requireFaultMatch;
    setRequireFaultMatch(newValue);
    checkChanges(daysWindow, newValue);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const days = parseInt(daysWindow) || 30;
    if (days < 1) {
      toast.error('Days window must be at least 1');
      return;
    }
    updateMutation.mutate({
      repeatedServiceDaysWindow: days,
      repeatedServiceRequireFaultMatch: requireFaultMatch,
    });
  };

  const handleReset = () => {
    if (settings) {
      setDaysWindow(String(settings.repeatedServiceDaysWindow));
      setRequireFaultMatch(settings.repeatedServiceRequireFaultMatch);
      setHasChanges(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <RefreshCw className="w-6 h-6 text-purple-600" />
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Repeated Service Detection</h2>
          <p className="text-sm text-gray-500">
            Configure how the system detects repeated/warranty services for the same device
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Days Window */}
        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
          <h3 className="text-sm font-medium text-gray-900 mb-2">Lookback Window</h3>
          <p className="text-sm text-gray-500 mb-4">
            Number of days to look back from the delivery date when checking for previous services on the same device.
          </p>
          <div className="relative max-w-xs">
            <input
              type="number"
              value={daysWindow || ''}
              onChange={(e) => handleDaysChange(e.target.value)}
              min="1"
              step="1"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="30"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">days</span>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            A service delivered within the last {parseInt(daysWindow) || 30} days will be considered for repeated service detection.
          </p>
        </div>

        {/* Fault Match Toggle */}
        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-900">Require Fault Match</h3>
              <p className="text-sm text-gray-500 mt-1">
                When enabled, a service is only flagged as repeated if the new service has at least one matching fault from the previous service. When disabled, any new service for a recently-serviced device is flagged.
              </p>
            </div>
            <button
              type="button"
              onClick={handleFaultMatchToggle}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                requireFaultMatch ? 'bg-purple-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  requireFaultMatch ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          <div className="mt-3">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              requireFaultMatch
                ? 'bg-purple-100 text-purple-800'
                : 'bg-amber-100 text-amber-800'
            }`}>
              {requireFaultMatch ? 'Strict — same fault required' : 'Broad — any device match flags as repeated'}
            </span>
          </div>
        </div>

        {/* Info Box */}
        <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">How Repeated Service Detection Works:</p>
            <ul className="mt-2 space-y-1 list-disc list-inside text-blue-700">
              <li>When creating a new service, the system checks for previous delivered services within the lookback window</li>
              <li>Detection matches by exact device OR by same customer + same phone model</li>
              {requireFaultMatch ? (
                <li>Only flags as repeated if at least one fault matches the previous service (e.g., same "Screen Replacement")</li>
              ) : (
                <li>Flags as repeated for any device match, regardless of fault type</li>
              )}
              <li>Repeated services are auto-flagged as potential warranty repairs</li>
            </ul>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t">
          <button
            type="button"
            onClick={handleReset}
            disabled={!hasChanges || updateMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reset
          </button>
          <button
            type="submit"
            disabled={!hasChanges || updateMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {updateMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
